import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { FilterSelectNative } from '@/components/custom/filter';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { FilterCheckBox } from '@/components/custom/filter-checkbox/filter-checkbox';
import ChallengeDataGrid from './ChallengeDataGrid';
import { useClient } from 'jsonapi-react';
import { useSession } from '@/hooks/useSession';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';

export default function Challenges() {
  const [challenges, setChallenges] = useState([]);
  const [themes, setThemes] = useState([]);
  const [orderByPeriod, setOrderByPeriod] = useState('descendent');
  const [orderByParticipants, setOrderByParticipants] = useState('descendent');
  const [param, setParam] = useState('period');
  const [loading, setLoading] = useState(false);
  const client = useClient();
  const { session } = useSession();

  const filters = useFormik({
    initialValues: {
      searchTerm: '',
      theme: '',
      status: {
        actual: false,
        scheduled: false,
        expired: false
      },
      'my-challenges': false
    }
  });

  const statusOptions = [
    {
      label: 'Vigente',
      value: filters.values.status.actual,
      onChange: () => filters.setFieldValue('status', { ...filters.values.status, actual: !filters.values.status.actual })
    },
    {
      label: 'Agendado',
      value: filters.values.status.scheduled,
      onChange: () => filters.setFieldValue('status', { ...filters.values.status, scheduled: !filters.values.status.scheduled })
    },
    {
      label: 'Expirado',
      value: filters.values.status.expired,
      onChange: () => filters.setFieldValue('status', { ...filters.values.status, expired: !filters.values.status.expired })
    }
  ];

  function createDate(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const fetchChallenges = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('/challenges');
    if (error) {
      toast.error('Erro ao buscar desafios');
    } else {
      setChallenges(
        data.map(item => {
          return { ...item, 'initial-date': createDate(item['initial-date']), 'end-date': createDate(item['end-date']) };
        })
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  useEffect(() => {
    setThemes(challenges.map(item => ({ id: item.taxonomy.id, name: item.taxonomy.name })).filter((value, index, self) => index === self.findIndex(t => t.id === value.id && t.name === value.name)));
  }, [challenges]);

  const filteredChallenges = useMemo(() => {
    const { status } = filters.values;
    const statusList = [];

    const items = {
      actual: 'vigente',
      expired: 'expirado',
      scheduled: 'agendado'
    };

    Object.keys(status).map(key => {
      if (status[key]) {
        statusList.push(items[key]);
      }
    });

    const filterByText = challenges.filter(item => filters.values.searchTerm === '' || item.title.toLowerCase().includes(filters.values.searchTerm.toLowerCase()));

    const filterByTheme = filterByText.filter(item => filters.values.theme === '' || item.taxonomy.id === parseInt(filters.values.theme));

    const filterByStatus = filterByTheme.filter(item => statusList.length === 0 || statusList.includes(item.status));

    const filterByAuthorship = filterByStatus.filter(item => {
      if (filters.values['my-challenges']) {
        return item.user.id === session.user.id;
      } else return true;
    });

    return filterByAuthorship;
  }, [challenges, filters.values]);

  const orderedChallenges = useMemo(() => {
    let newChallenges;
    if (param === 'participants' && orderByParticipants === 'ascendent') {
      newChallenges = filteredChallenges.sort((a, b) => b?.['participation-count'] - a?.['participation-count']);
    }
    if (param === 'participants' && orderByParticipants === 'descendent') {
      newChallenges = filteredChallenges.sort((a, b) => a?.['participation-count'] - b?.['participation-count']);
    }
    if (param === 'period' && orderByPeriod === 'ascendent') {
      newChallenges = filteredChallenges.sort((a, b) => new Date(a['end-date']) - new Date(b['end-date']));
    }
    if (param === 'period' && orderByPeriod === 'descendent') {
      newChallenges = filteredChallenges.sort((a, b) => new Date(b['end-date']) - new Date(a['end-date']));
    }
    return newChallenges;
  }, [orderByPeriod, orderByParticipants, filteredChallenges, param]);

  return (
    <>
      <div className="filter-bar filter-bar--borderless">
        <div className="filter-bar__inner">
          <div className="filter-bar__row">
            <label
              className="filter-bar__label"
              htmlFor="search"
            >
              {t('textsCommon.research')}
            </label>
            <input
              className="form__control form__control--search-with-icon"
              placeholder={t('textsCommon.research')}
              type="search"
              name="search"
              id="search"
              value={filters.values.searchTerm}
              onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
            />
          </div>

          <FilterSelectNative
            optionBlank={''}
            label={'Tema'}
            blankLabel={'Selecione um tema'}
            value={filters.values.theme}
            onChange={e => filters.setFieldValue('theme', e.target.value)}
            options={themes}
          />

          <FilterSelectMulti
            label="Status"
            placeholder="Selecione o status"
            data={statusOptions}
          />

          <FilterCheckBox
            label="Meus desafios"
            value={filters.values['my-challenges']}
            onChange={() => filters.setFieldValue('my-challenges', !filters.values['my-challenges'])}
          />
        </div>

        <div className="filter-bar__actions">
          <Link
            className="btn btn--wide btn--primary btn--tab-side"
            to="/desafio-da-semana/criar"
          >
            Criar desafio
          </Link>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : challenges.length > 0 ? (
        <ChallengeDataGrid
          setParam={setParam}
          orderByParticipants={orderByParticipants}
          orderByPeriod={orderByPeriod}
          setOrderByParticipants={setOrderByParticipants}
          setOrderByPeriod={setOrderByPeriod}
          challenges={orderedChallenges}
          setChallenges={setChallenges}
        />
      ) : (
        <EmptyState type="data" />
      )}
    </>
  );
}
